import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { money } from "@/lib/formatters";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Package, Wrench, Plus, Search, Loader2, Edit2, Trash2, Power, PowerOff, TrendingUp,
} from "lucide-react";

// Debe coincidir con SERVICE_CATEGORIES del backend
const FALLBACK_CATEGORIES = [
  { key: "ingenieria", label: "Ingeniería eléctrica" },
  { key: "subestaciones", label: "Subestaciones y transformadores" },
  { key: "medidores", label: "Medidores KV y KV2C" },
  { key: "redes_mt_bt", label: "Redes de media y baja tensión" },
  { key: "seccionamiento_sf6", label: "Seccionamiento en SF₆" },
  { key: "empalmes", label: "Empalmes, terminales y mufas" },
  { key: "monitoreo", label: "Monitoreo de redes" },
];
const UNITS = ["PZA", "SERVICIO", "LOTE", "ML", "M2", "KG", "HR"];

function CatalogTab({ kind, canWrite, categories }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [showActive, setShowActive] = useState("all"); // all | active | inactive
  const [editing, setEditing] = useState(null); // null | { …item }
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ kind });
      if (q) params.set("q", q);
      if (category !== "all" && kind === "servicio") params.set("category", category);
      if (showActive === "active") params.set("active_only", "true");
      const r = await api.get(`/catalog?${params.toString()}`);
      let list = r.data;
      if (showActive === "inactive") list = list.filter((x) => !x.active);
      setItems(list);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [kind, q, category, showActive]);

  const del = async (item) => {
    const msg = item.times_used > 0
      ? `Este ítem ya se usó en ${item.times_used} cotización(es). Se marcará como INACTIVO (no se borra). ¿Continuar?`
      : "¿Eliminar este ítem del catálogo? (todavía no se usa en ninguna cotización)";
    if (!confirm(msg)) return;
    try {
      const r = await api.delete(`/catalog/${item.id}`);
      toast.success(r.data.message || (r.data.soft ? "Ítem inactivado" : "Ítem eliminado"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const toggleActive = async (item) => {
    try {
      await api.patch(`/catalog/${item.id}`, { active: !item.active });
      toast.success(item.active ? "Ítem inactivado" : "Ítem activado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-4" data-testid={`catalog-${kind}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input placeholder="Buscar por nombre, descripción o SKU…" value={q}
                  onChange={(e) => setQ(e.target.value)} data-testid={`catalog-${kind}-search`}
                  className="pl-8 h-10" />
        </div>
        {kind === "servicio" && (
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-56 h-10" data-testid={`catalog-${kind}-category`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las categorías</SelectItem>
              {categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={showActive} onValueChange={setShowActive}>
          <SelectTrigger className="w-40 h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Activos</SelectItem>
            <SelectItem value="inactive">Inactivos</SelectItem>
          </SelectContent>
        </Select>
        {canWrite && (
          <Button onClick={() => setShowNew(true)} className="bg-[#005A91] hover:bg-[#004a7a] text-white font-bold ml-auto" data-testid={`catalog-${kind}-new`}>
            <Plus className="h-4 w-4 mr-1.5" /> Nuevo {kind}
          </Button>
        )}
      </div>

      <div className="pe-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-600 text-left text-xs uppercase tracking-wider">
              <th className="px-3 py-3">Nombre</th>
              {kind === "servicio" && <th className="px-3 py-3">Categoría</th>}
              {kind === "producto" && <th className="px-3 py-3">SKU</th>}
              <th className="px-3 py-3">Unidad</th>
              <th className="px-3 py-3 text-right">P. unitario</th>
              <th className="px-3 py-3 text-right">Usado</th>
              <th className="px-3 py-3">Estado</th>
              {canWrite && <th className="px-3 py-3 text-right">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={canWrite ? 8 : 7} className="text-center py-12 text-slate-400"><Loader2 className="animate-spin inline mr-2" />Cargando…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={canWrite ? 8 : 7} className="text-center py-16 text-slate-400">
                <div className="mb-2">Aún no hay {kind === "servicio" ? "servicios" : "productos"} en el catálogo.</div>
                {canWrite && <div className="text-xs">Empieza dando de alta con el botón <b className="text-[#005A91]">Nuevo {kind}</b> arriba a la derecha.</div>}
              </td></tr>
            ) : items.map((it) => (
              <tr key={it.id} data-testid={`catalog-row-${it.id}`}
                  className={`border-t border-slate-100 hover:bg-slate-50 ${!it.active ? "opacity-60" : ""}`}>
                <td className="px-3 py-3">
                  <div className="font-bold">{it.name}</div>
                  {it.description && <div className="text-xs text-slate-500 line-clamp-2 max-w-md">{it.description}</div>}
                </td>
                {kind === "servicio" && (
                  <td className="px-3 py-3 text-xs">
                    {(categories.find((c) => c.key === it.category) || {}).label || it.category || "—"}
                  </td>
                )}
                {kind === "producto" && (
                  <td className="px-3 py-3 font-mono text-xs">{it.sku || "—"}</td>
                )}
                <td className="px-3 py-3 uppercase text-xs text-slate-600">{it.unit}</td>
                <td className="px-3 py-3 text-right pe-numeric font-bold">{money(it.unit_price)}</td>
                <td className="px-3 py-3 text-right pe-numeric text-xs">
                  {it.times_used > 0 ? (
                    <span className="text-[#005A91] font-bold flex items-center justify-end gap-1">
                      <TrendingUp className="h-3 w-3" /> {it.times_used}
                    </span>
                  ) : <span className="text-slate-400">0</span>}
                </td>
                <td className="px-3 py-3">
                  {it.active ? (
                    <span className="inline-flex px-2 py-0.5 rounded text-xs font-bold border bg-emerald-50 text-emerald-700 border-emerald-300">Activo</span>
                  ) : (
                    <span className="inline-flex px-2 py-0.5 rounded text-xs font-bold border bg-slate-100 text-slate-500 border-slate-300">Inactivo</span>
                  )}
                </td>
                {canWrite && (
                  <td className="px-3 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(it)}
                              data-testid={`catalog-edit-${it.id}`}
                              className="h-8 px-2 text-[#005A91] hover:bg-[#005A91]/10">
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleActive(it)}
                              data-testid={`catalog-toggle-${it.id}`}
                              className="h-8 px-2 text-slate-500 hover:bg-slate-100">
                        {it.active ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => del(it)}
                              data-testid={`catalog-delete-${it.id}`}
                              className="h-8 px-2 text-slate-500 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CatalogItemDialog
        open={showNew || !!editing}
        onOpenChange={(o) => { if (!o) { setShowNew(false); setEditing(null); } }}
        kind={kind}
        item={editing}
        categories={categories}
        onSaved={() => { setShowNew(false); setEditing(null); load(); }}
      />
    </div>
  );
}

function CatalogItemDialog({ open, onOpenChange, kind, item, categories, onSaved }) {
  const isEdit = !!item;
  const [form, setForm] = useState({
    name: "", description: "", category: "", unit: "PZA", unit_price: "", sku: "", active: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (item) {
      setForm({
        name: item.name || "", description: item.description || "",
        category: item.category || "", unit: item.unit || "PZA",
        unit_price: item.unit_price != null ? String(item.unit_price) : "",
        sku: item.sku || "", active: item.active !== false,
      });
    } else {
      setForm({ name: "", description: "", category: kind === "servicio" ? categories[0]?.key || "" : "",
                unit: kind === "producto" ? "PZA" : "SERVICIO", unit_price: "", sku: "", active: true });
    }
  }, [open, item, kind, categories]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Falta el nombre");
    if (kind === "servicio" && !form.category) return toast.error("Selecciona una categoría");
    setSaving(true);
    try {
      const body = {
        kind, name: form.name.trim(),
        description: form.description || "",
        unit: (form.unit || "PZA").toUpperCase(),
        unit_price: Number(form.unit_price || 0),
        active: form.active,
      };
      if (kind === "servicio") body.category = form.category;
      if (kind === "producto") body.sku = form.sku || undefined;
      if (isEdit) {
        await api.patch(`/catalog/${item.id}`, body);
        toast.success("Ítem actualizado");
      } else {
        await api.post("/catalog", body);
        toast.success(`${kind === "servicio" ? "Servicio" : "Producto"} agregado al catálogo`);
      }
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="catalog-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#005A91] font-black">
            {isEdit ? "Editar" : `Nuevo ${kind === "servicio" ? "servicio" : "producto"}`}
          </DialogTitle>
          <DialogDescription>
            {kind === "servicio"
              ? "Los servicios aparecerán al crear una cotización de tipo Servicio."
              : "Los productos aparecerán al crear una cotización de tipo Producto."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <FieldC label="Nombre *" full>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="catalog-name" />
          </FieldC>
          <FieldC label="Descripción" full>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                        data-testid="catalog-description" />
          </FieldC>
          {kind === "servicio" && (
            <FieldC label="Categoría *" full>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="catalog-category"><SelectValue placeholder="Elige…" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </FieldC>
          )}
          {kind === "producto" && (
            <FieldC label="SKU / Código">
              <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })}
                      data-testid="catalog-sku" placeholder="Opcional" />
            </FieldC>
          )}
          <FieldC label="Unidad">
            <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
              <SelectTrigger data-testid="catalog-unit"><SelectValue /></SelectTrigger>
              <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select>
          </FieldC>
          <FieldC label="Precio base *" full>
            <Input type="number" step="0.01" value={form.unit_price}
                    onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                    data-testid="catalog-price" className="text-lg font-bold" />
          </FieldC>
          <label className="col-span-2 flex items-center gap-2 text-sm text-slate-600 py-1">
            <input type="checkbox" checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    data-testid="catalog-active" className="h-4 w-4 accent-[#005A91]" />
            Ítem activo (aparece al agregar líneas en nuevas cotizaciones)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="catalog-save"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {isEdit ? "Guardar cambios" : "Agregar al catálogo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const FieldC = ({ label, children, full }) => (
  <div className={`space-y-1 ${full ? "col-span-2" : ""}`}>
    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</Label>
    {children}
  </div>
);

export default function Catalog() {
  const { can } = useAuth();
  const canWrite = can("catalog_write");
  const [tab, setTab] = useState("servicio");
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);

  useEffect(() => {
    api.get("/catalog/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6 max-w-6xl" data-testid="catalog-page">
      <div>
        <div className="text-[11px] font-black tracking-[0.24em] uppercase text-[#005A91]">Módulo</div>
        <h1 className="text-3xl lg:text-4xl font-black text-[#0A1118] tracking-tight flex items-center gap-3">
          <Package className="h-8 w-8 text-[#005A91]" />
          Catálogo
        </h1>
        <p className="text-slate-500 mt-1 text-sm max-w-2xl">
          Da de alta tus servicios y productos con precio base. Al crear una cotización, se pueden
          insertar líneas prellenadas desde aquí; la cantidad y descuento por línea quedan
          editables sin alterar el catálogo maestro.
          {!canWrite && <span className="block mt-1 italic text-slate-400">Consulta únicamente — no tienes permiso para editar el catálogo.</span>}
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="servicio" data-testid="cat-tab-servicio"><Wrench className="h-3.5 w-3.5 mr-1.5" /> Servicios</TabsTrigger>
          <TabsTrigger value="producto" data-testid="cat-tab-producto"><Package className="h-3.5 w-3.5 mr-1.5" /> Productos</TabsTrigger>
        </TabsList>
        <TabsContent value="servicio" className="mt-6"><CatalogTab kind="servicio" canWrite={canWrite} categories={categories} /></TabsContent>
        <TabsContent value="producto" className="mt-6"><CatalogTab kind="producto" canWrite={canWrite} categories={categories} /></TabsContent>
      </Tabs>
    </div>
  );
}
